import React, { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Zap, Plus, RotateCcw, Info } from "lucide-react";
import { api, formatApiError } from "../lib/api";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { EvChargeForm } from "../components/ev/EvChargeForm";
import { EvKpis, EvSidePanels } from "../components/ev/EvSummary";
import { EvChargesTable } from "../components/ev/EvChargesTable";

const MONTHS = ["Gennaio", "Febbraio", "Marzo", "Aprile", "Maggio", "Giugno", "Luglio", "Agosto", "Settembre", "Ottobre", "Novembre", "Dicembre"];
const now = new Date();
const trig = "bg-[#0C0B08] border-[#D4A72C]/25 text-slate-100 h-10";

const Filter = ({ label, value, onChange, items, testid }) => (
  <div className="min-w-0">
    <div className="text-[11px] uppercase tracking-[0.18em] text-[#C9A961] mb-1.5">{label}</div>
    <Select value={value} onValueChange={onChange}>
      <SelectTrigger className={trig} data-testid={testid}><SelectValue /></SelectTrigger>
      <SelectContent className="bg-slate-950 border-slate-800 max-h-72">{items.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent>
    </Select>
  </div>
);

export default function RicaricheEV() {
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [driverId, setDriverId] = useState("all");
  const [vehicleId, setVehicleId] = useState("all");
  const [drivers, setDrivers] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [rows, setRows] = useState([]);
  const [sum, setSum] = useState(null);
  const [form, setForm] = useState(null);

  useEffect(() => {
    api.get("/drivers").then((r) => setDrivers(r.data || [])).catch(() => {});
    api.get("/ev-charges/vehicles").then((r) => setVehicles(r.data || [])).catch(() => {});
  }, []);

  const load = useCallback(async () => {
    const params = { year, month, ...(driverId !== "all" ? { driver_id: driverId } : {}), ...(vehicleId !== "all" ? { vehicle_id: vehicleId } : {}) };
    try {
      const [a, b] = await Promise.all([api.get("/ev-charges", { params }), api.get("/ev-charges/summary", { params })]);
      setRows(a.data); setSum(b.data);
    } catch (e) { toast.error(formatApiError(e)); }
  }, [year, month, driverId, vehicleId]);
  useEffect(() => { load(); }, [load]);

  const del = async (r) => {
    if (!window.confirm(`Eliminare la ricarica del ${r.date} di ${r.driver_name}?`)) return;
    try { await api.delete(`/ev-charges/${r.id}`); toast.success("Ricarica eliminata"); load(); } catch (e) { toast.error(formatApiError(e)); }
  };
  const openReceipt = async (r) => {
    try {
      const res = await api.get(`/ev-charges/${r.id}/receipt`, { responseType: "blob" });
      window.open(URL.createObjectURL(res.data), "_blank");
    } catch (e) { toast.error("Ricevuta non disponibile"); }
  };
  const years = Array.from({ length: 5 }, (_, i) => now.getFullYear() - 3 + i);

  return (
    <div className="space-y-5 md:space-y-6" data-testid="ev-page">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div className="flex items-start gap-3">
          <Zap size={34} className="text-[#E9C46A] mt-1" />
          <div>
            <h1 className="font-display text-3xl sm:text-4xl text-white">Ricariche veicoli elettrici</h1>
            <p className="text-sm text-slate-400 mt-1">Registro delle ricariche dei veicoli elettrici della flotta</p>
          </div>
        </div>
        <button type="button" onClick={() => setForm({})} className="h-11 px-5 rounded-xl btn-gold-primary font-bold inline-flex items-center gap-2" data-testid="ev-new-btn"><Plus size={16} strokeWidth={3} />Nuova ricarica</button>
      </div>

      {sum && <EvKpis s={sum} />}

      <div className="grid grid-cols-1 xl:grid-cols-[1fr_340px] gap-4 md:gap-5">
        <div className="space-y-4 min-w-0">
          <div className="rounded-2xl border border-[#D4A72C]/20 bg-[#0A0907]/90 p-4 grid grid-cols-2 md:grid-cols-[1fr_0.7fr_1fr_1fr_auto] gap-3 items-end" data-testid="ev-filters">
            <Filter label="Mese" value={String(month)} onChange={(v) => setMonth(Number(v))} items={MONTHS.map((m, i) => [String(i + 1), m])} testid="ev-filter-month" />
            <Filter label="Anno" value={String(year)} onChange={(v) => setYear(Number(v))} items={years.map((y) => [String(y), String(y)])} testid="ev-filter-year" />
            <Filter label="Autista" value={driverId} onChange={setDriverId} items={[["all", "Tutti gli autisti"], ...drivers.map((d) => [d.id, `${d.first_name} ${d.last_name}`])]} testid="ev-filter-driver" />
            <Filter label="Veicolo" value={vehicleId} onChange={setVehicleId} items={[["all", "Tutti i veicoli elettrici"], ...vehicles.map((v) => [v.id, `${v.label} · ${v.plate}`])]} testid="ev-filter-vehicle" />
            <button type="button" onClick={() => { setDriverId("all"); setVehicleId("all"); setMonth(now.getMonth() + 1); setYear(now.getFullYear()); }}
              className="h-10 px-3 rounded-lg border border-[#D4A72C]/40 text-[#E9C46A] text-sm inline-flex items-center gap-1.5 hover:bg-[#D4A72C]/10" data-testid="ev-filter-reset"><RotateCcw size={14} />Azzera</button>
          </div>
          <EvChargesTable rows={rows} onEdit={(r) => setForm(r)} onDelete={del} onReceipt={openReceipt} />
        </div>
        {sum && <EvSidePanels s={sum} />}
      </div>

      <div className="rounded-xl border border-white/10 bg-white/[0.02] px-4 py-3 text-xs text-slate-400 inline-flex items-start gap-2" data-testid="ev-independence-note">
        <Info size={15} className="text-[#E9C46A] shrink-0" />Le ricariche sono un registro indipendente: non influiscono su conteggi autisti, fornitori o contabilità.
      </div>

      {form && <EvChargeForm charge={form.id ? form : null} drivers={drivers} vehicles={vehicles} onClose={(saved) => { setForm(null); if (saved) load(); }} />}
    </div>
  );
}
